import Box from "@material-ui/core/Box"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import IconButton from "@material-ui/core/IconButton"
import Typography from "@material-ui/core/Typography"
import CloseIcon from "@material-ui/icons/Close"
import PropTypes from "prop-types"
import moment from "moment"
import React, { useContext, useEffect, useState } from "react"
import { connect } from "react-redux"
import {
  Button,
  Field,
  FormSection,
  LoadingIndicator,
  RadioGroup,
  TitleSection
} from "~/components/"
import { updateSettings } from "~/actions/settingsActions"
import ShelfApi from "~/utils/ShelfApi"
import { InfotextContext } from "../context/InfoTextContext"
import { useTranslation } from "react-i18next"

const InventoriesView = props => {
  const { t } = useTranslation()
  const { setInfotexts } = useContext(InfotextContext)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [iframeUrl, setIframeUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [inventoryData, setInventoryData] = useState(null)
  const [contactPerson, setContactPerson] = useState("")
  const [isActionLoading, setIsActionLoading] = useState(false)

  const [errors, setErrors] = useState({})

  useEffect(() => {
    getData()
    getMetaBaseUrl()
  }, [])

  function getData() {
    ShelfApi.get("/inventories/current")
      .then(res => {
        if (!res.data || res.data.is_completing || !res.data.id) {
          setInventoryData(null)
          setContactPerson("")
        } else {
          setInventoryData(res.data)
          setContactPerson(res.data.contact_person || "")
        }
        setLoading(false)
      })
      .catch(err => {
        setInventoryData(null)
        setContactPerson("")
        setLoading(false)
      })
  }
  function getMetaBaseUrl() {
    ShelfApi.get("/inventories/dashboard")
      .then(res => {
        setIframeUrl(res.data)
      })
      .catch(() => setIframeUrl(null))
  }

  function handleStart() {
    setIsActionLoading(true)
    ShelfApi.post("/inventories", {
      inventory: { contact_person: contactPerson }
    })
      .then(res => {
        getData()
        setInfotexts()
        setIsActionLoading(false)
      })
      .catch(() => setIsActionLoading(false))
  }

  function handleUpdate(active, isCompleting = false) {
    setIsActionLoading(true)
    ShelfApi.put(`/inventories/${inventoryData.id}`, {
      inventory: {
        ...inventoryData,
        active: active,
        is_completing: isCompleting,
        valid_until: active ? null : inventoryData.valid_until
      }
    })
      .then(res => {
        if (isCompleting) {
          setContactPerson("")
        }
        getData()
        setIsConfirmDialogOpen(false)
        setInfotexts()
        setIsActionLoading(false)
      })
      .catch(() => {
        setIsActionLoading(false)
        setIsConfirmDialogOpen(false)
      })
  }

  function handleClose() {
    setIsConfirmDialogOpen(false)
  }

  const isRunning =
    inventoryData &&
    inventoryData.id &&
    !inventoryData.valid_until &&
    !inventoryData.is_completing
  const isPaused =
    inventoryData &&
    inventoryData.id &&
    inventoryData.valid_until &&
    !inventoryData.is_completing
  const isNone = !inventoryData || !inventoryData.id || inventoryData.is_completing

  return (
    <div>
      <TitleSection>
        <h3>{t("inventories.title")}</h3>
      </TitleSection>

      {loading && <LoadingIndicator />}

      {isConfirmDialogOpen && (
        <Dialog
          onClose={handleClose}
          aria-labelledby="confirmation-dialog-title"
          open={isConfirmDialogOpen}
          fullWidth
        >
          <DialogTitle id="confirmation-dialog-title" variant="h2">
            <Box
              display="flex"
              justifyContent="space-between"
              alignContent="center"
              alignItems="center"
            >
              <div>{t("inventories.completeMode")}</div>
              <IconButton
                alignSelf="flex-end"
                aria-label="close"
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {t("inventories.confirmFinish")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button secondary onClick={handleClose}>
              {t("no")}
            </Button>
            <Button
              primary
              loading={isActionLoading}
              onClick={() => handleUpdate(false, true)}
            >
              {t("yes")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <FormSection label={t("inventories.inventory")}>
        {isNone && (
          <Box display="flex" alignItems="flex-end">
            <Box flex={1} maxWidth={400} mr={2}>
              <Field
                label={t("inventories.contactPerson")}
                name="contact_person"
                value={contactPerson}
                onChange={e => setContactPerson(e.target.value)}
              />
            </Box>
            <Button
              primary
              icon="play"
              loading={isActionLoading}
              onClick={handleStart}
            >
              {t("inventories.start")}
            </Button>
          </Box>
        )}
        {(isRunning || isPaused) && (
          <Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t("inventories.startedAt")}
              </Typography>
              <Typography variant="h5">
                {moment(inventoryData.valid_from).format("DD.MM.YYYY – HH:mm")}
                {" Uhr"}
              </Typography>
            </Box>
            <Box display="flex" mt={3}>
              {isRunning && (
                <Box mr={2}>
                  <Button
                    primary
                    icon="pause"
                    loading={isActionLoading}
                    onClick={() => handleUpdate(false)}
                  >
                    {t("inventories.pause")}
                  </Button>
                </Box>
              )}
              {isPaused && (
                <Box mr={2}>
                  <Button
                    primary
                    icon="redo"
                    loading={isActionLoading}
                    onClick={() => handleUpdate(true)}
                  >
                    {t("inventories.restart")}
                  </Button>
                </Box>
              )}
              <Button
                primary
                icon="check"
                loading={isActionLoading}
                onClick={() => setIsConfirmDialogOpen(true)}
              >
                {t("inventories.end")}
              </Button>
            </Box>
          </Box>
        )}
      </FormSection>

      {!!iframeUrl && (
        <iframe
          title="metabase dashboard"
          src={iframeUrl}
          frameBorder={0}
          scrolling="no"
          width="100%"
          height="1500px"
          allowTransparency
        />
      )}
    </div>
  )
}

InventoriesView.propTypes = {
  user: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
}

const mapStateToProps = ({ auth }) => {
  const { user } = auth
  const { settings } = user
  return { user, settings }
}

export default connect(mapStateToProps, { updateSettings })(InventoriesView)



// WEBPACK FOOTER //
// ./src/views/InventoriesView.js